import React from 'react';
import { Box, Button, CircularProgress, Typography } from '../ui';
import { EventVerificationConfig } from '../../utils/eventVerification';

interface EventVerificationPanelProps {
  verification: EventVerificationConfig;
  codeAttempt: string;
  codeError: string | null;
  attestationChecked: boolean;
  verifyLoading: boolean;
  onCodeChange: (value: string) => void;
  onAttestationChange: (checked: boolean) => void;
  onVerify: () => void;
}

const EventVerificationPanel: React.FC<EventVerificationPanelProps> = ({
  verification,
  codeAttempt,
  codeError,
  attestationChecked,
  verifyLoading,
  onCodeChange,
  onAttestationChange,
  onVerify,
}) => {
  return (
    <Box
      sx={{
        mt: 4,
        width: '100%',
        maxWidth: 480,
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        p: 3,
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
      }}
    >
      <Box>
        <Typography variant="subtitle1" fontWeight={700}>
          Mark your attendance
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {verification.codeEnabled && verification.attestationEnabled
            ? 'Enter the code shared during the event, or tick the attestation below.'
            : verification.codeEnabled
              ? 'Enter the code shared during the event.'
              : 'Tick the box below to confirm you attended.'}
        </Typography>
      </Box>

      {verification.codeEnabled && (
        <Box>
          <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ display: 'block', mb: 0.5, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
            Confirmation code
          </Typography>
          <Box
            component="input"
            type="text"
            value={codeAttempt}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              onCodeChange(e.target.value);
            }}
            placeholder="Enter code"
            autoCapitalize="characters"
            spellCheck={false}
            sx={{
              width: '100%',
              px: 1.5,
              py: 1.25,
              fontSize: '1rem',
              fontFamily: 'monospace',
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              border: '1px solid',
              borderColor: codeError ? 'error.main' : 'divider',
              borderRadius: 1,
              outline: 'none',
              transition: 'border-color 120ms ease',
              '&:focus': {
                borderColor: codeError ? 'error.main' : 'primary.main',
              },
            }}
          />
          {codeError && (
            <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
              {codeError}
            </Typography>
          )}
        </Box>
      )}

      {verification.attestationEnabled && (
        <Box
          component="label"
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 1.25,
            cursor: 'pointer',
            p: 1.5,
            borderRadius: 1,
            border: '1px solid',
            borderColor: attestationChecked ? 'primary.main' : 'divider',
            bgcolor: attestationChecked ? 'rgba(var(--brand-rgb), 0.08)' : 'grey.50',
            transition: 'border-color 120ms ease, background-color 120ms ease',
          }}
        >
          <Box
            component="input"
            type="checkbox"
            checked={attestationChecked}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              onAttestationChange(e.target.checked)
            }
            sx={{ mt: 0.5, cursor: 'pointer' }}
          />
          <Typography variant="body2">
            {verification.attestationText.trim() ||
              'I attended this event in full and engaged with the material.'}
          </Typography>
        </Box>
      )}

      <Button
        variant="contained"
        color="primary"
        onClick={onVerify}
        disabled={
          verifyLoading ||
          // Block submit when nothing is on the table yet.
          (!codeAttempt.trim() && !attestationChecked)
        }
        startIcon={verifyLoading ? <CircularProgress size={18} color="inherit" /> : undefined}
      >
        {verifyLoading ? 'Verifying…' : 'Mark complete'}
      </Button>
    </Box>
  );
};

export default EventVerificationPanel;
